// [ai] The widget host controller (one per app, main.tsx): builds the AtlasHost every frame hands its widget — queries through
// the batched client, media through the one player and the focus arbiter, open through the router or the player, state in
// localStorage per widget, ask through Astra — and owns the frames' status store the seam reads. Frames are widgets/Frame.tsx.
import { createContext, useContext } from "react";
import type { Astra } from "../chat/astra";
import { isAppHref } from "../app/links";
import { parseLink } from "../core/links";
import type { Player } from "../player/player";
import { query } from "../api/query";
import type { AtlasEvents, AtlasHost, MediaState, Off, Size, Theme } from "../sdk/types";
import { createArbiter, type Arbiter } from "./focus";
import { createWidgetsStore, type WidgetsState } from "../stores/widgets";
import type { StoreApi } from "zustand";

type KV = Pick<Storage, "getItem" | "setItem">;
export type WidgetsDeps = {
  player: Player; astra: Astra; navigate: (to: string) => void;
  importModule?: (url: string) => Promise<unknown>;   // a trusted module's compiled ESM (tests hand in components)
  localStore?: KV | null; origin?: () => string; scheme?: () => Theme["scheme"]; onScheme?: (fn: (t: Theme) => void) => Off;
};
/** What a frame gives the host about itself: its measured box, and how to tell its widget to yield its sound. */
export type FrameHooks = { size: () => Size; onSize: (fn: (s: Size) => void) => Off; onFocus: (fn: (has: boolean) => void) => Off; pauseOwner: () => void };
const KEY = (k: string) => `dossier.widget.${k}`;
const darkMq = () => (typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null);
export const defaultImport = (url: string): Promise<unknown> => import(/* @vite-ignore */ url);

export class Widgets {
  readonly store: StoreApi<WidgetsState> = createWidgetsStore(); readonly arbiter: Arbiter; readonly importModule: (url: string) => Promise<unknown>; readonly origin: () => string;
  private readonly d: WidgetsDeps; private readonly kv: KV | null; private readonly live = new Map<string, () => Record<string, unknown>>();   // a mounted frame's state cache (the truth while it lives; storage is the copy)
  constructor(d: WidgetsDeps) {
    this.d = d; this.importModule = d.importModule ?? defaultImport; this.origin = d.origin ?? (() => location.origin);
    this.kv = d.localStore === undefined ? (typeof localStorage !== "undefined" ? localStorage : null) : d.localStore;
    const e = d.player.engine;
    this.arbiter = createArbiter({ playing: () => e.playing, pause: () => e.pause(), resume: () => void e.play() });
    e.on("play", () => { this.arbiter.hostStarted(); this.syncFocus(); });
  }
  syncFocus() { this.store.getState().setFocus(this.arbiter.owner); }
  /** A widget's persisted keys (this browser). */
  stateOf(key: string): Record<string, unknown> { const l = this.live.get(key); if (l) return l(); try { const raw = this.kv?.getItem(KEY(key)); const v: unknown = raw ? JSON.parse(raw) : {}; return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {}; } catch { return {}; } }
  private write(key: string, obj: Record<string, unknown>) { try { this.kv?.setItem(KEY(key), JSON.stringify(obj)); } catch { /* private mode: the session keeps it in memory only */ } }
  /** "Edit" on a frame: the file lands in Astra's composer. */
  edit(path: string, title: string) { this.d.astra.ask(`Edit the widget "${title}" in ${path}: `); }
  scheme(): Theme["scheme"] { return this.d.scheme ? this.d.scheme() : darkMq()?.matches ? "dark" : "light"; }
  private onScheme(fn: (t: Theme) => void): Off {
    if (this.d.onScheme) return this.d.onScheme(fn);
    const mq = darkMq(); if (!mq) return () => {}; const on = () => fn({ scheme: mq.matches ? "dark" : "light" }); mq.addEventListener("change", on); return () => mq.removeEventListener("change", on);
  }
  private mediaState(): MediaState { const n = this.d.player.state.now; return { rec: n.item?.id ?? null, title: n.item?.title ?? null, playing: n.playing, t: n.t, duration: n.duration }; }

  /** The AtlasHost for one frame (key = "<view>/<widget id>"). */
  host(key: string, frame: FrameHooks): AtlasHost {
    const p = this.d.player, e = p.engine; let cache = this.stateOf(key); this.live.set(key, () => cache);
    const owner = { id: key, pause: () => frame.pauseOwner() };
    const on = <K extends keyof AtlasEvents>(event: K, fn: (...a: AtlasEvents[K]) => void): Off => {
      switch (event) {
        case "time": return e.on("time", fn as (t: number) => void);
        case "size": return frame.onSize(fn as (s: Size) => void);
        case "theme": return this.onScheme(fn as (t: Theme) => void);
        case "focus": return frame.onFocus(fn as (has: boolean) => void);
        default: throw new Error(`unknown event "${String(event)}"`);
      }
    };
    return {
      query: (name, params = {}) => query(name, params),
      media: {
        play: async ({ rec, t, e: end }) => { await p.open({ kind: "rec", id: rec, t: t ?? 0, e: end ?? null }); },
        pause: () => e.pause(), seek: t => e.seek(t), state: () => this.mediaState(),
        on: ((event: "time" | "state", fn: (...a: never[]) => void) => {
          if (event === "time") return e.on("time", fn as (t: number) => void);
          const l = () => (fn as (s: MediaState) => void)(this.mediaState()); const offs = (["play", "pause", "load", "ended"] as const).map(ev => e.on(ev, l)); return () => { for (const off of offs) off(); };
        }) as AtlasHost["media"]["on"],
        focus: () => { this.arbiter.focus(owner); this.syncFocus(); }, release: () => { this.arbiter.release(key); this.syncFocus(); },
      },
      open: href => { const link = parseLink(href); if (link) { void p.open(link); return; } const app = isAppHref(href); if (app) { this.d.navigate(app); return; } throw new Error(`open: "${href}" is not a page of this app or a rec:/note: link`); },
      state: { get: <T,>(k: string) => cache[k] as T | undefined, set: (k, v) => { cache = { ...cache, [k]: v }; this.write(key, cache); } },
      theme: () => ({ scheme: this.scheme() }), ask: prompt => this.d.astra.ask(prompt), size: () => frame.size(), on,
    };
  }
  /** A frame went away: its cache with it. */
  forget(key: string) { this.live.delete(key); }
  dump() { const s = this.store.getState(); return { focus: s.focus, frames: Object.values(s.frames).map(f => (f.error === undefined ? { id: f.id, kind: f.kind, status: f.status } : { id: f.id, kind: f.kind, status: f.status, error: f.error })) }; }
}
export const WidgetsContext = createContext<Widgets | null>(null);
export function useWidgetsCtl(): Widgets { const w = useContext(WidgetsContext); if (!w) throw new Error("WidgetsContext is missing"); return w; }
